import cx from 'classnames';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useMemo } from 'react';

import useIsMobile from '../../../../hooks/use-is-mobile';
import useLinkProps from '../../../../hooks/use-link-props';
import { categoryCounterReducer } from '../../../../utils/category-counter-reduce';
import { footerCurve } from '../../../modules/footer/footer-upper-curve';
import NoStock from '../../../modules/no-stock-message';
import Trustpilot from '../../../modules/trustpilot';
import Breadcrumbs from '../../../modules/v2/breadcrumbs';
import ValueProposition from '../../../modules/value-proposition';
import { useAppContext } from '../../../providers/app-context';
import CategoryNavigation from '../../../ui/v2/categories/navigation';
import BPImage from '../../../ui/v2/image';
import LastEntries from '../vehicle/last-entries';
import LastVehicles from '../../v2/vehicle/last-vehicles';

import CategoriesSchema from './seo';
import PageTitleSEO from '../../../ui/v2/typography/page-title';
import BrandHistory from '../../../modules/v2/brand-history';
import { getBrandHistory } from '../../../../services/brand-history';
import FlowTitleSEO from '../../../ui/v2/flow-title';
import SEOContent from './seo-content-section';
import dynamic from 'next/dynamic';
import SetAlertInfo from '../create-part-alert/index_new';

const TrustBox = dynamic(() => import('../../../modules/trustpilot/new'), { ssr: false });

const Categories = ({
  groups,
  carInfo,
  lastEntries,
  vehicles,
  vehicle,
  stock,
  info,
}) => {
  const {
    features: { isBrandEnabled },
    businessValues,
  } = useAppContext();
  const [, setVariant] = useAtom(footerCurve);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const { locale = 'en', query, route } = useRouter();
  const brandHistory = getBrandHistory({
    locale: locale,
    slug: carInfo?.brand.alias,
  });

  useEffect(() => {
    setVariant(carInfo ? 'SECONDARY' : 'PRIMARY');
  }, [carInfo, setVariant]);

  const breadcrumbItems = [];

  if (carInfo) {
    if (!isBrandEnabled) {
      breadcrumbItems.push({
        ...getLink('brands'),
        text: t('common:brand.other'),
      });
    }

    if (carInfo.model) {
      breadcrumbItems.push({
        ...getLink('models', { brand: carInfo.brand.slug }),
        text: t('common:model.breadrumb', {
          brand: `${carInfo.brand.name}`,
        }),
      });
    }

    breadcrumbItems.push({
      ...getLink(carInfo.model.have_versions ? 'versions' : 'models', {
        brand: carInfo.brand.slug,
        model: carInfo.model.slug,
      }),
      text: `${carInfo.model.name} ${carInfo.version ? carInfo.version.name : ''
        }`,
    });
  }

  const totalStock = useMemo(() => {
    if (stock && stock[0]?.stock) {
      return stock[0].stock;
    } else {
      return Object.values(categoryCounterReducer(groups)).reduce(
        (t, n) => (t += n),
        0
      );
    }
  }, [stock, groups]);

  return (
    <main>
      <CategoriesSchema
        carInfo={carInfo}
        breadcrumbs={breadcrumbItems}
        count={totalStock}
      />
      <Breadcrumbs
        items={breadcrumbItems}
        currentPage={t('common:partscatalog')}
      />
      <div
        className={cx({
          'mb-6': !!carInfo,
        })}
      >
        <FlowTitleSEO
          isTitleContainsImage={!isMobile && carInfo}
          imageContainer={
            !isMobile &&
            carInfo && (
              <div
                className={cx(
                  'relative',
                  'aspect-video',
                  'shrink-0',
                  'flex basis-5/12',
                  'w-full bg-neutral-5'
                )}
              >
                <BPImage
                  src={
                    carInfo?.model?.image ||
                    `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                  }
                  layout="fill" objectFit="contain"
                  objectPosition="center"
                  fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                  className={cx('bg-cover mix-blend-multiply')}
                  alt={t('common:images_alt.vehicle_alt', {
                    vehicle: `${carInfo?.brand.name} ${carInfo?.model?.name || ''
                      } ${carInfo?.version?.name || ''}`,
                  })}
                />
              </div>
            )
          }
          cols={[
            carInfo ? (
              <PageTitleSEO
                key="title"
                textAlign={carInfo && !isMobile ? 'left' : 'center'}
                title={t('common:categories.pageTitle', {
                  brand: `${carInfo.brand.name} `,
                })}
                subtitle={t('common:categories.pageSubTitle', {
                  model: carInfo.model?.name || '',
                  version: carInfo.version?.name || '',
                  start_date: carInfo.model?.start_date,
                  end_date: carInfo.model?.end_date || new Date().getFullYear(),
                })}
              />
            ) : (
              <PageTitleSEO key="title" title={t('categories:title')} />
            ),
            carInfo && isMobile && (
              <div
                className={cx(
                  'relative',
                  'aspect-video',
                  'shrink-0',
                  'flex basis-5/12',
                  'w-full bg-neutral-5'
                )}
              >
                <BPImage
                  src={
                    carInfo?.model?.image ||
                    `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                  }
                  layout="fill" objectFit="contain"
                  objectPosition="center"
                  fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                  className={cx('bg-cover mix-blend-multiply')}
                  alt={t('common:images_alt.vehicle_alt', {
                    vehicle: `${carInfo?.brand.name} ${carInfo?.model?.name} ${carInfo?.version?.name}`,
                  })}
                />
              </div>
            ),
            totalStock > 0 ? (
              <h2
                key="stock"
                className="max-lg:text-center text-base lg:text-xl"
              >
                <Trans
                  i18nKey={
                    carInfo
                      ? 'common:selectCategoryFromStock'
                      : 'common:selectCategoryFromStockWithoutVehicle'
                  }
                  components={{ strong: <strong /> }}
                  values={{
                    count: totalStock.toLocaleString(locale),
                    brand: carInfo?.brand.name || '',
                    vehicle: `${carInfo?.model.name || ''} ${carInfo?.version ? carInfo.version?.name : ''
                      } ${carInfo
                        ? `[${carInfo?.model.start_date}-${carInfo?.model.end_date || new Date().getFullYear()
                        }]`
                        : ''
                      }
                    `,
                    totalStockAvailable:
                      businessValues.totalStockAvailable.defaultValue,
                  }}
                />
              </h2>
            ) : (
              <div className="-ml-4 -my-9 max-lg:text-center text-base lg:text-xl">
                <NoStock
                  vehicle={carInfo}
                  selection={null}
                  groups={groups}
                  createAlert={false}
                />
              </div>
            ),
            totalStock <= 0 && (
              <div className="max-lg:text-center lg:mt-0">
                <SetAlertInfo
                  brand={carInfo.brand}
                  model={carInfo.model}
                  version={carInfo.version}
                  part={info?.subCategory || ''}
                />
              </div>
            ),
          ]}
        />
      </div>
      {groups && (
        <div
          className={cx('container', 'mx-auto', 'max-lg:px-4 lg:pl-4', 'py-12')}
        >
          <CategoryNavigation
            groups={groups}
            slug={vehicle}
            splitInColumns={true}
            isSearchFullWidth={true}
            showNoResultsButton={false}
            carInfo={carInfo}
            linkToNavigate={carInfo ? undefined : 'brands-with-part'}
          />
        </div>
      )}
      {lastEntries && lastEntries.length > 0 && (
        <LastEntries products={lastEntries} vehicle={carInfo || {}} />
      )}
      <ValueProposition
        infoToDisplayInTitle={`${carInfo?.brand.name || ''} ${carInfo?.model?.name || ''
          } ${carInfo?.version?.name || ''}`}
      />
      {vehicles && vehicles.length > 0 && (
        <LastVehicles products={vehicles} vehicle={carInfo || {}} />
      )}
      <section className="w-full bg-blue-10 py-12 lg:py-16 text-justify ">
        {brandHistory && carInfo && (
          <BrandHistory
            imageURL={carInfo?.brand?.logo_url}
            {...brandHistory}
            brand={carInfo?.brand?.name}
            vehicle={carInfo}
            stock={stock && stock[1]?.stock.toLocaleString(locale)}
          />
        )}
        <SEOContent
          withCarInfo={!!carInfo}
          vehicle={carInfo}
          count={stock && stock[1]?.stock.toLocaleString(locale)}
        ></SEOContent>
      </section>
      <div className="bg-white">
        <div className={cx('py-8 lg:py-6')}>
          <div className={cx('container', 'mx-auto', 'text-center')}>
            <TrustBox />
          </div>
        </div>
      </div>
    </main>
  );
};

export default Categories;
